// LevelEarnedToast — T-095 §4C / ADR-0013 D6. Listens for
// `calicool:level-earned` CustomEvents on `window` and shows the
// tier that was just earned and why. Mounted above the routes in
// AppShell so it survives the navigation that `onResolved` fires
// on workout complete. Not rendered while a workout is active
// (defensive; current workouts are single-exercise so this is a
// no-op today, but a multi-exercise hold workout could earn a
// tier mid-session and we never want a competing toast during a
// set).

import { useEffect, useState } from "react";
import type { JSX } from "react";
import { useI18n } from "../../i18n";
import { isWorkoutActive } from "../../hooks/workoutActive";

export interface LevelEarnedDetail {
  exerciseId: string;
  tier: "none" | "beginner" | "intermediate" | "mastery";
  was: "none" | "beginner" | "intermediate" | "mastery";
  reps: number;
  seconds: number;
  isHold: boolean;
  sets: number;
}

interface ActiveToast {
  id: number;
  detail: LevelEarnedDetail;
}

let nextId = 1;

export function LevelEarnedToast(): JSX.Element {
  const { t } = useI18n();
  const [active, setActive] = useState<ActiveToast | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<LevelEarnedDetail>).detail;
      if (!detail) return;
      if (isWorkoutActive()) return; // no competing toast during a set
      const id = nextId++;
      setActive({ id, detail });
      window.setTimeout(() => {
        setActive((current) => (current?.id === id ? null : current));
      }, 6000);
    };
    window.addEventListener("calicool:level-earned", handler);
    return () => window.removeEventListener("calicool:level-earned", handler);
  }, []);

  if (!active) return <></>;
  const { detail } = active;
  const tierLabel = t(`tree.${detail.tier}`);
  const text = detail.isHold
    ? t("level.earnedHold", {
        tier: tierLabel,
        seconds: detail.seconds,
      })
    : t("level.earned", {
        tier: tierLabel,
        sets: detail.sets,
        reps: detail.reps,
      });

  return (
    <div
      data-testid="level-toast"
      role="status"
      className="fixed inset-x-0 bottom-0 z-50 mx-auto flex w-full max-w-[640px] flex-col items-stretch gap-3 border border-[var(--color-lime)] bg-[var(--color-slab)] p-4 shadow-2xl"
      style={{ borderRadius: "var(--radius-card) var(--radius-card) 0 0" }}
    >
      <p
        data-testid="level-toast-text"
        className="font-sans text-base text-[var(--color-ink-hi)]"
        style={{ lineHeight: 1.4 }}
      >
        {text}
      </p>
      <div className="flex justify-end">
        <button
          type="button"
          data-testid="level-toast-dismiss"
          onClick={() => setActive(null)}
          className="border border-[var(--color-lime)] bg-[var(--color-lime)] px-4 py-2 font-subhead text-sm font-bold uppercase tracking-[0.06em] text-[var(--color-void)]"
          style={{ borderRadius: "var(--radius-sharp)", minHeight: "44px" }}
        >
          {t("pwa.close")}
        </button>
      </div>
    </div>
  );
}